import { useCallback, useState } from "react";
import { api } from "../../../api";
import { Button } from "../../../externalComponents/tailwind/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../externalComponents/tailwind/dialog";
import { Fieldset, Label } from "../../../externalComponents/tailwind/fieldset";
import {
  Radio,
  RadioField,
  RadioGroup,
} from "../../../externalComponents/tailwind/radio";

const DialogManageConsent = ({
  currentConsent,
  contactNumber,
  bsuid,
  isModalOpen,
  onClose,
  onSuccess,
}) => {
  const getCurrentConsentValue = () => {
    if (currentConsent?.hasOptIn === true) {
      return "optin";
    }
    if (currentConsent?.optoutAt) {
      return "optout";
    }
    return "never_optin";
  };

  const [selectedConsent, setSelectedConsent] = useState(
    getCurrentConsentValue(),
  );
  const [isLoading, setIsLoading] = useState(false);

  const handleConsentChange = useCallback((value) => {
    setSelectedConsent(value);
  }, []);

  const handleSave = async () => {
    setIsLoading(true);

    try {
      if (selectedConsent === "optin") {
        await api.optInContact({
          contactNumber,
          bsuid,
          isPhoneNumberEncrypted: true,
        });
      } else if (selectedConsent === "optout") {
        await api.optOutContact({
          contactNumber,
          bsuid,
          isPhoneNumberEncrypted: true,
          optoutReason: "manual",
        });
      }

      if (onSuccess) {
        onSuccess(selectedConsent);
      }

      onClose();
    } catch (error) {
      console.error("Error updating contact consent:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog key="dialog_manage_consent" open={isModalOpen} onClose={onClose}>
      <DialogTitle>Manage Contact Consent</DialogTitle>
      <DialogBody className="flex-1 overflow-y-auto scrollbar">
        <p className="mb-4">Choose the consent status for this contact:</p>

        <Fieldset>
          <RadioGroup
            name="consent_status"
            defaultValue={getCurrentConsentValue()}
            onChange={handleConsentChange}
          >
            <RadioField>
              <Radio value="optin" />
              <Label>
                <span>Opt-in</span>
              </Label>
            </RadioField>

            <RadioField>
              <Radio value="optout" />
              <Label>
                <span>Opt-out</span>
              </Label>
            </RadioField>

            <RadioField
              className={
                currentConsent?.hasOptIn === true || currentConsent?.optoutAt
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }
            >
              <Radio
                value="never_optin"
                disabled={
                  currentConsent?.hasOptIn === true || currentConsent?.optoutAt
                }
              />
              <Label>
                <span>Never opted in</span>
                {(currentConsent?.hasOptIn === true ||
                  currentConsent?.optoutAt) && (
                  <span className="text-sm text-gray-500 ml-2">
                    (Not available for contacts who have already opted in or
                    out)
                  </span>
                )}
              </Label>
            </RadioField>
          </RadioGroup>
        </Fieldset>
        {selectedConsent === "optin" && (
          <div className="my-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Important:</strong> Opting in a contact requires their
              explicit consent. Setting a contact to "Opt-in" will trigger the
              corresponding opt-in automation.
            </p>
          </div>
        )}
        {selectedConsent === "optout" && (
          <div className="my-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="text-sm text-orange-800">
              <strong>Note:</strong> Opting out a contact will exclude them from
              future campaign sends.
            </p>
          </div>
        )}
      </DialogBody>
      <DialogActions>
        <Button
          color="secondaryButton"
          onClick={() => {
            setSelectedConsent(getCurrentConsentValue());
            onClose();
          }}
          disabled={isLoading}
          className="min-w-[100px] h-10"
        >
          Cancel
        </Button>
        <Button
          color="primaryButton"
          onClick={handleSave}
          className="min-w-[100px] h-10"
          loading={isLoading}
          disabled={selectedConsent === getCurrentConsentValue()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogManageConsent;
